import React, { useEffect, useState, useCallback } from "react";
import { Type, Sparkles, Eye, Mail, Check } from "lucide-react";
import SubPageShell from "@/apps/rider/components/SubPageShell";

const STORAGE_TEXT = "link.a11y.text-size";    // "default" | "large" | "xlarge"
const STORAGE_MOTION = "link.a11y.reduced-motion"; // "0" | "1"

/**
 * /preferences/accessibility — local-device accessibility controls.
 *
 * Stored in localStorage (no backend) and applied to <html> via data
 * attributes the global stylesheet keys off:
 *   data-text-size="large|xlarge"   → boosts root font-size scale
 *   data-reduced-motion="1"         → disables non-essential CSS transitions/animations
 *
 * High-contrast support is detected from the OS via prefers-contrast
 * (read-only status — operating systems own the master switch).
 */
export default function AccessibilityPrefs() {
  const [textSize, setTextSize] = useState(() => readLS(STORAGE_TEXT, "default"));
  const [reducedMotion, setReducedMotion] = useState(() => readLS(STORAGE_MOTION, "0") === "1");
  const [osHighContrast, setOsHighContrast] = useState(false);
  const [osReducedMotion, setOsReducedMotion] = useState(false);

  // Apply current settings to <html>
  useEffect(() => {
    const root = document.documentElement;
    if (textSize === "default") root.removeAttribute("data-text-size");
    else root.setAttribute("data-text-size", textSize);
    try { localStorage.setItem(STORAGE_TEXT, textSize); } catch (_) {}
  }, [textSize]);

  useEffect(() => {
    const root = document.documentElement;
    if (reducedMotion) root.setAttribute("data-reduced-motion", "1");
    else root.removeAttribute("data-reduced-motion");
    try { localStorage.setItem(STORAGE_MOTION, reducedMotion ? "1" : "0"); } catch (_) {}
  }, [reducedMotion]);

  // OS-level capability detection.
  useEffect(() => {
    const w = window;
    if (!w?.matchMedia) return;
    const hc = w.matchMedia("(prefers-contrast: more)");
    const rm = w.matchMedia("(prefers-reduced-motion: reduce)");
    const updateHc = () => setOsHighContrast(hc.matches);
    const updateRm = () => setOsReducedMotion(rm.matches);
    updateHc(); updateRm();
    hc.addEventListener?.("change", updateHc);
    rm.addEventListener?.("change", updateRm);
    return () => {
      hc.removeEventListener?.("change", updateHc);
      rm.removeEventListener?.("change", updateRm);
    };
  }, []);

  return (
    <SubPageShell
      eyebrow="Preferences"
      title="Accessibility"
      subtitle="Adjust text size and motion for a more comfortable Link experience."
      back="/account"
      backLabel="Account"
      testId="accessibility-page"
    >
      {/* Text size */}
      <section className="bg-white border hairline rounded-2xl p-4" data-testid="a11y-text-size">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 inline-flex items-center justify-center bg-[#FCEADE] text-[#ed6500] rounded-xl shrink-0">
            <Type size={16} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[#0A1128] text-[15px]">Text size</div>
            <div className="text-xs text-[#515E7B] mt-0.5">Increase the type scale across the rider app.</div>
            <div className="grid grid-cols-3 gap-1.5 mt-3" role="radiogroup" aria-label="Text size">
              {[
                { id: "default", label: "Default", sample: 14 },
                { id: "large",   label: "Large",   sample: 16 },
                { id: "xlarge",  label: "Larger",  sample: 18 },
              ].map((opt) => {
                const selected = textSize === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setTextSize(opt.id)}
                    className={`px-2 py-3 rounded-xl border text-center transition-colors ${
                      selected
                        ? "bg-[#0A1128] text-[#F9F7F4] border-[#0A1128]"
                        : "bg-[#F9F7F4] text-[#0A1128] border-[#E5E1D8] hover:border-[#ed6500]/50"
                    }`}
                    data-testid={`a11y-text-size-${opt.id}`}
                    data-selected={selected}
                  >
                    <span style={{ fontSize: opt.sample }}>Aa</span>
                    <div className="text-[10px] tracking-[0.18em] uppercase font-semibold mt-1">{opt.label}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Reduced motion */}
      <section className="bg-white border hairline rounded-2xl p-4 mt-3" data-testid="a11y-reduced-motion">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 inline-flex items-center justify-center bg-[#FCEADE] text-[#ed6500] rounded-xl shrink-0">
            <Sparkles size={16} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-[#0A1128] text-[15px]">Reduce motion</div>
                <div className="text-xs text-[#515E7B] mt-0.5">Minimize animations and transitions.</div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={reducedMotion}
                onClick={() => setReducedMotion((v) => !v)}
                data-testid="a11y-reduced-motion-toggle"
                data-checked={reducedMotion ? "1" : "0"}
                className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
                  reducedMotion ? "bg-[#ed6500]" : "bg-[#0A1128]/15"
                }`}
              >
                <span
                  className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform"
                  style={{ transform: reducedMotion ? "translateX(20px)" : "translateX(0)" }}
                />
              </button>
            </div>
            {osReducedMotion && (
              <div className="mt-2 text-[11px] text-[#ed6500]">
                Your operating system also requests reduced motion — Link respects that automatically.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contrast status */}
      <section className="bg-white border hairline rounded-2xl p-4 mt-3" data-testid="a11y-contrast">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 inline-flex items-center justify-center bg-[#FCEADE] text-[#ed6500] rounded-xl shrink-0">
            <Eye size={16} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[#0A1128] text-[15px]">High contrast</div>
            <div className="text-xs text-[#515E7B] mt-0.5">
              {osHighContrast
                ? "Your operating system requests high contrast — Link is rendering with stronger borders and text."
                : "Toggle high contrast on your device. Link automatically follows your OS setting."}
            </div>
            {osHighContrast && (
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#ed6500] tracking-[0.18em] uppercase font-semibold">
                <Check size={11} /> Active
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Help contact */}
      <section className="bg-[#0A1128] text-[#F9F7F4] rounded-2xl p-4 mt-3" data-testid="a11y-help">
        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#ed6500] mb-1">Need help?</div>
        <div className="text-[14px] leading-snug">
          Have an accessibility need we don't yet support? Tell us — we work commute-by-commute.
        </div>
        <a
          href="/support/contact"
          className="mt-3 inline-flex items-center gap-2 bg-[#ed6500] text-white px-4 py-2 rounded-lg text-[11px] tracking-[0.18em] uppercase font-semibold"
          data-testid="a11y-contact-link"
        >
          <Mail size={12} /> Contact support
        </a>
      </section>
    </SubPageShell>
  );
}

function readLS(key, fallback) {
  if (typeof localStorage === "undefined") return fallback;
  try { return localStorage.getItem(key) ?? fallback; } catch (_) { return fallback; }
}
